import './styles/EntitlementAssistantDrawer.scss';

import {
  ArrowsInSimpleIcon,
  ArrowsOutSimpleIcon,
  SidebarSimpleIcon,
  XIcon,
} from '@phosphor-icons/react';
import { Tooltip } from '@whistler-ui/k2/components/Tooltip/Tooltip';
import { Heading } from '@whistler-ui/k2/components/Typography';
import { cn } from '@whistler-ui/k2/helpers/classBuilders';
import { motion } from 'motion/react';
import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

import type { EntitlementAssistantContext } from '../../../store/AI/entitlementAssistant/useEntitlementAssistantStore';
import { useEntitlementAssistantStore } from '../../../store/AI/entitlementAssistant/useEntitlementAssistantStore';
import { useEntitlementsStore } from '../../../store/entitlements';

const tooltipContentClass = cn('!p-0', 'mt-1.5', 'mb-1.5');

const tooltipWrapperClass = cn('relative', 'inline-block');

type EntitlementAssistantPanelProps = {
  contextKey: string;
  context: EntitlementAssistantContext;
};

const EntitlementAssistantPanel = ({
  contextKey,
  context,
}: EntitlementAssistantPanelProps) => {
  const {
    closeDrawer,
    toggleMaximizeDrawer,
    toggleBottomDrawer,
    toggleMaximizeVerticalDrawer,
  } = useEntitlementAssistantStore();
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const { content, maximized, open, onBottom, maximizedVertical } = context;

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }

    const scrollToBottom = () => {
      el.scrollTop = el.scrollHeight;
    };

    scrollToBottom();

    const observer = new MutationObserver(() => {
      scrollToBottom();
    });

    observer.observe(el, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  const handleCloseDrawer = useCallback(() => {
    closeDrawer(contextKey);
  }, [closeDrawer, contextKey]);

  const handleMaximize = useCallback(() => {
    toggleMaximizeDrawer(contextKey);
  }, [toggleMaximizeDrawer, contextKey]);

  const handleToggleBottom = useCallback(() => {
    toggleBottomDrawer(contextKey);
  }, [toggleBottomDrawer, contextKey]);

  const handleMaximizeVertical = useCallback(() => {
    toggleMaximizeVerticalDrawer(contextKey);
  }, [toggleMaximizeVerticalDrawer, contextKey]);

  return (
    <motion.div
      hidden={!open}
      inert={!open}
      data-entitlement-assistant={open ? 'present' : undefined}
      initial={false}
      animate={{
        opacity: open ? 1 : 0,
        pointerEvents: open ? 'auto' : 'none',
      }}
      transition={{ duration: 0.9, ease: 'easeInOut' }}
      className={cn(
        'inset-0',
        'z-15',
        'pointer-events-none',
        // The squeeze utilities pad the page through a `~` sibling selector,
        // which `hidden` does not switch off, so only the visible panel may
        // carry them.
        open && {
          'presentation-padding-r-400': !maximized && !onBottom,
          'sm:presentation-padding-r-400 md:presentation-padding-r-800 layout-hide':
            maximized,
          'fixed layout-space-b-500': onBottom,
          'max-h-[70%] top-[calc(30%)] max-md:max-h-[40%] max-md:top-[calc(60%)]':
            onBottom && maximizedVertical,
          'max-h-[20%] top-[calc(80%)]': onBottom && !maximizedVertical,
        },
      )}
    >
      <div
        className={cn(
          'inset-0',
          'flex',
          'z-15',
          'absolute',
          'items-center',
          'justify-center',
          'pointer-events-none',
          'items-stretch',
          'justify-end',
          {
            fixed: !onBottom,
          },
          {
            absolute: onBottom,
          },
        )}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Entitlements Assistant"
          className={cn(
            'pointer-events-auto',
            'flex',
            'flex-col',
            'bg-white',
            'shadow-(--box-shadow-default)',
            {
              'w-full max-w-[400px] md:max-w-[800px] max-h-none h-full rounded-none':
                maximized,
            },
            {
              'w-full max-w-[400px] max-h-none h-full rounded-none':
                !maximized && !onBottom,
            },
            {
              'w-full max-w-none shadow-[0_-8px_12px_-4px_rgba(0,0,0,0.2)]':
                !maximized && onBottom,
            },
          )}
        >
          <div className="c-EntitlementAssistantDrawer c-EntitlementAssistantDrawer__header flex items-center justify-between px-5 py-4 pr-2">
            <Heading level={3} className="flex items-center gap-2 ">
              <svg
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="19"
                viewBox="0 0 20 19"
                fill="none"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M5.47975 9.47975L7.50012 4.84192L9.52032 9.47975L14.2297 11.5001L9.52032 13.5203L7.50012 18.5001L5.47975 13.5203L0.841919 11.5001L5.47975 9.47975Z"
                  fill="white"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M14.2297 3.22975L15.5001 0.841919L16.7703 3.22975L19.1581 4.50012L16.7703 5.77032L15.5001 8.15815L14.2297 5.77032L11.8419 4.50012L14.2297 3.22975Z"
                  fill="white"
                />
              </svg>
              <span className="text-white">Entitlements Assistant</span>
            </Heading>
            <div className="flex items-center gap-1">
              {onBottom && (
                <Tooltip
                  align="top"
                  delay={0.4}
                  wrapperClassName={tooltipWrapperClass}
                  contentClassName={tooltipContentClass}
                  trigger={
                    <button
                      onClick={handleMaximizeVertical}
                      aria-label={maximizedVertical ? 'Minimize' : 'Maximize'}
                      className="rounded-md p-1 text-sm text-gray-600 hover:cursor-pointer hover:bg-white/15"
                    >
                      {maximizedVertical ? (
                        <ArrowsInSimpleIcon
                          size={20}
                          color="#fff"
                          weight="regular"
                          className="rotate-135"
                        />
                      ) : (
                        <ArrowsOutSimpleIcon
                          size={20}
                          color="#fff"
                          weight="regular"
                          className="rotate-135"
                        />
                      )}
                    </button>
                  }
                  content={
                    <>
                      <div className="relative bg-gray-700 text-white text-xs px-3 py-1.5 rounded-md shadow-md">
                        {maximizedVertical ? 'Minimize' : 'Maximize'}
                      </div>
                      <div
                        className={cn('absolute', {
                          '-top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-gray-700 rotate-45':
                            !onBottom,
                          'absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-gray-700 rotate-45':
                            onBottom,
                        })}
                      ></div>
                    </>
                  }
                />
              )}
              {!onBottom && (
                <Tooltip
                  align="top"
                  delay={0.4}
                  wrapperClassName={tooltipWrapperClass}
                  contentClassName={tooltipContentClass}
                  trigger={
                    <button
                      onClick={handleMaximize}
                      aria-label={maximized ? 'Minimize' : 'Maximize'}
                      className="rounded-md p-1 text-sm text-gray-600 hover:cursor-pointer hover:bg-white/15"
                    >
                      {maximized ? (
                        <ArrowsInSimpleIcon
                          size={20}
                          color="#fff"
                          weight="regular"
                          className="rotate-45"
                        />
                      ) : (
                        <ArrowsOutSimpleIcon
                          size={20}
                          color="#fff"
                          weight="regular"
                          className="rotate-45"
                        />
                      )}
                    </button>
                  }
                  content={
                    <>
                      <div className="relative bg-gray-700 text-white text-xs px-3 py-1.5 rounded-md shadow-md">
                        {maximized ? 'Minimize' : 'Maximize'}
                      </div>
                      <div
                        className={cn('absolute', {
                          '-top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-gray-700 rotate-45':
                            !onBottom,
                          'absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-gray-700 rotate-45':
                            onBottom,
                        })}
                      ></div>
                    </>
                  }
                />
              )}

              <Tooltip
                align="top"
                wrapperClassName={tooltipWrapperClass}
                contentClassName={tooltipContentClass}
                delay={0.4}
                trigger={
                  <button
                    onClick={handleToggleBottom}
                    aria-label={onBottom ? 'Move to right' : 'Move to bottom'}
                    className="rounded-md p-1 text-sm text-gray-600 hover:cursor-pointer hover:bg-white/15"
                  >
                    {onBottom ? (
                      <SidebarSimpleIcon
                        size={20}
                        color="#fff"
                        mirrored
                        weight="regular"
                      />
                    ) : (
                      <SidebarSimpleIcon
                        size={20}
                        color="#fff"
                        weight="regular"
                        style={{ transform: 'rotate(-90deg)' }}
                      />
                    )}
                  </button>
                }
                content={
                  <>
                    <div className="relative bg-gray-700 text-white text-xs px-3 py-1.5 rounded-md shadow-md">
                      {onBottom ? 'Move to right' : 'Move to bottom'}
                    </div>
                    <div
                      className={cn('absolute', {
                        '-top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-gray-700 rotate-45':
                          !onBottom,
                        'absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-gray-700 rotate-45':
                          onBottom,
                      })}
                    ></div>
                  </>
                }
              />

              <button
                onClick={handleCloseDrawer}
                aria-label="Close"
                className="rounded-md p-1 text-sm text-gray-600 hover:cursor-pointer hover:bg-white/15"
              >
                <XIcon size={24} color="#fff" weight="regular" />
              </button>
            </div>
          </div>

          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto text-md leading-6 text-gray-800"
          >
            <div className="text-(--color-font-primary) h-full">{content}</div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export const EntitlementAssistantDrawer = () => {
  const { contexts, showForPathname, closeDrawersForOtherEnvironments } =
    useEntitlementAssistantStore();
  const { pathname } = useLocation();
  const environmentId = useEntitlementsStore(state => state.current?.id);

  useEffect(() => {
    showForPathname(pathname);
  }, [pathname, showForPathname]);

  useEffect(() => {
    closeDrawersForOtherEnvironments(environmentId);
  }, [environmentId, closeDrawersForOtherEnvironments]);

  return (
    <>
      {Object.keys(contexts).map(key => (
        <EntitlementAssistantPanel
          key={key}
          contextKey={key}
          context={contexts[key]}
        />
      ))}
    </>
  );
};
